import React, {createContext, useContext} from 'react';

import {isMultiValue} from '../../util/filters';
import {FunctionalOperators} from '../../util/filtersDefaults';
import {Filter, FilterKey, IndividualFilter} from '../../util/filterTypes';

type BaseFilterActions = {
  deleteFilter: (index: number) => void;
  setFilter: (
    index: number,
    newFilterValue: IndividualFilter<FilterKey>
  ) => void;
};

type ExtendedFilterActions = BaseFilterActions & {
  getFilter: (index: number) => IndividualFilter<FilterKey>;
};
export type TagOperators = '=' | '!=' | 'IN' | 'NIN';

// @ts-ignore
const EditableFilterContext = createContext<ExtendedFilterActions>({});

export const EditableFilterContextProvider = ({
  actions,
  children,
  filters,
}: {
  actions: BaseFilterActions;
  children: React.ReactNode;
  filters: Array<IndividualFilter<FilterKey>>;
}) => {
  return (
    <EditableFilterContext.Provider
      value={{
        ...actions,
        getFilter: (index: number) => filters[index],
      }}>
      {children}
    </EditableFilterContext.Provider>
  );
};

export function useEditableFilterContext(filterIndex: number) {
  const context = useContext(EditableFilterContext);
  if (!context) {
    console.log('Editable Filter Used Outside of Context Tree');
  }

  /**
   * If the context changes or the filter changes the render cycle will fire so we don't need internal state to handle the filter selection
   */
  const filter = context.getFilter(filterIndex);

  return [
    {
      filter,
      isMultiValueFilter: isMultiValue(filter),
    },
    {
      changeFilterOperation: (
        newFilterOperation: keyof typeof FunctionalOperators
      ) => {
        /**
         * The logic here is complicated because the value type can change depending on if the Op is a single-or-multiple value operation.
         */
        const opsToValueMap: Record<TagOperators, boolean | string[]> = {
          '=': true,
          '!=': false,
          IN: [],
          NIN: [],
        };
        context.setFilter(filterIndex, {
          ...filter,
          key: {
            section: 'tags',
            name: '',
          },
          op: newFilterOperation as TagOperators,
          // @ts-ignore
          value: opsToValueMap[newFilterOperation],
        });
      },
      deleteFilter: () => context.deleteFilter(filterIndex),
      disableFilter: () => {
        // @ts-ignore ignoring this as this definitely exists but the types don't guarantee
        context.setFilter(filterIndex, {
          ...filter,
          disabled: !filter.disabled,
        });
      },
      setFilterValueIndividual: (newValue: string) => {
        // @ts-ignore ignoring this as this definitely exists but the types don't guarantee
        context.setFilter(filterIndex, {
          ...filter,
          key: {
            // @ts-ignore ignoring this as this definitely exists but the types don't guarantee
            section: filter.key.section,
            name: newValue,
          },
          disabled: newValue !== '' ? false : true,
        } as Filter<FilterKey>);
      },
      // We strip out the "*" when reading in the filter value to the input to avoid rending an accidental tag
      // however we have to reattach it on the way back out as the view won't update without it
      setFilterValueMulti: (newValue: string[]) => {
        // @ts-ignore ignoring this as this definitely exists but the types don't guarantee
        context.setFilter(filterIndex, {
          ...filter,
          key: {
            section: 'tags',
            name: '*',
          },
          value: [...newValue],
        } as Filter<FilterKey>);
      },
    },
  ] as const;
}
